<template>
  <div>
    <div v-if="initialized">
      <div class="row justify-content-end">
        <div class="col-12 col-m-3 col-xl-2 mb-3 mb-m-0">
          <date-picker
            :date="startDate"
            :label="translate('lblMinDate')"
            :format="dateFormat"
            @change="setStartDate"
          ></date-picker>
        </div>
        <div class="col-12 col-m-3 col-xl-2">
          <date-picker
            :date="endDate"
            :label="translate('lblMaxDate')"
            :format="dateFormat"
            @change="setEndDate"
          ></date-picker>
        </div>
      </div>
      <sortable-table
        v-if="filteredOrderHistoryTableData && filteredOrderHistoryTableData.length > 0"
        :table-header="tableHeader"
        :table-data="filteredOrderHistoryTableData"
        :additional-classes="additionalClasses"
        :locale="locale"
        :page-size="paging.pageSize"
        :page-size-options="paging.pageSizeOptions"
        :max-pages="paging.maxPages"
        @update-page-size="updatePageSize"
      ></sortable-table>
      <div class="mt-5" v-else>
        <p v-text="translate('lblNoResults')"></p>
      </div>
    </div>
    <div v-if="apiError" class="col-12 error-box mb-3" v-text="translate('lblApiError')"></div>
  </div>
</template>

<script>
import LoyaltyUserApiConnectorMixin from './mixins/LoyaltyApiConnectorMixin';
import SortableTable from '../shared/components/tables/SortableTable.vue';
import DatePicker from './components/DatePicker.vue';

export default {
  name: 'OrderHistory',
  mixins: [LoyaltyUserApiConnectorMixin],
  data: () => ({
    tableHeader: [
      // {
      //   name: 'code',
      // },
      {
        name: 'date',
        isSortable: true,
        sortInitially: 'desc',
        type: 'date',
      },
      {
        name: 'products',
        isSortable: true,
        type: 'alphanumeric',
      },
      {
        name: 'points',
      },
    ],
    startDate: null,
    endDate: null,
    orderHistoryTableData: [], // complete table data returned by api
    filteredOrderHistoryTableData: [], // filtered table data
    initialized: false,
    apiError: false,
  }),
  props: {
    additionalClasses: String,
    locale: String,
    dateFormat: {
      type: String,
      default: 'DD.MM.YYYY',
    },
    translations: Object,
    paging: Object,
  },
  methods: {
    async populateTable() {
      const result = await this.getUserApiResult();
      if (result) {
        const tableData = result.transactions;
        if (tableData) {
          this.orderHistoryTableData = tableData
            .map((entry) => {
              const mDate = window.moment(entry.date, 'YYYY-MM-DDThh:mm:ss.SSS');
              let pointText;
              const pointClasses = [];
              switch (entry.status?.toUpperCase()) {
                case 'PENDING':
                  pointText = this.translate('lblTransactionPending');
                  break;
                case 'DECLINED':
                  pointText = this.translate('lblTransactionDeclined');
                  break;
                default:
                  pointText = this.formatPoints(entry);
                  pointClasses.push('bold');
              }

              return {
                // code: {
                //   value: entry.printedCode,
                //   text: entry.printedCode || '-',
                //   additionalClasses: 'bold',
                // },
                date: {
                  value: mDate.toDate(),
                  text: mDate.format(this.dateFormat),
                },
                products: {
                  value: entry.touchpoint,
                  additionalClasses: 'touchpoint',
                },
                points: {
                  value: entry.points,
                  text: pointText,
                  additionalClasses: pointClasses,
                },
                status: entry.status,
              };
            });
          this.getPeriod();
          this.filterByDates();
        }
        this.initialized = true;
      }
    },
    /** *
     * Adds a space between sign and number.
     * @param entry
     * @returns {string}
     */
    formatPoints(entry) {
      if (entry && entry.points) {
        if (entry.type.toUpperCase() === 'ORDER') {
          return `<span class="point-value-negative">-&nbsp;${entry.points}</span>`;
        }
        if (entry.type.toUpperCase() === 'CORRECTION' && entry.points < 0) {
          return `<span class="point-value-negative">-&nbsp;${Math.abs(entry.points)}</span>`;
        }
        return `<span class="point-value-positive">+&nbsp;${Math.abs(entry.points)}</span>`;
      }
      return '<span class="point-value-positive">0</span>';
    },
    setStartDate(date) {
      this.startDate = date;
      this.filterByDates();
    },
    setEndDate(date) {
      this.endDate = date;
      this.filterByDates();
    },
    filterByDates() {
      this.filteredOrderHistoryTableData = [];
      this.$nextTick(() => {
        // set enddate to next day 0:00
        let endDate;
        if (this.endDate) {
          endDate = new Date(this.endDate.valueOf());
          endDate.setDate(endDate.getDate() + 1);
        }
        this.filteredOrderHistoryTableData = this.orderHistoryTableData.filter(
          (entry) => {
            const afterStartDate = (!this.startDate || entry.date.value >= this.startDate);
            const beforeEndDate = (!endDate || entry.date.value < endDate);
            return afterStartDate && beforeEndDate;
          },
        );
      });
    },
    updatePageSize(pageSize) {
      // eslint-disable-next-line
      this.paging.pageSize = pageSize;
    },
    getPeriod() {
      if (this.orderHistoryTableData.length > 0) {
        this.startDate = new Date(Math.min(...this.orderHistoryTableData.map((transaction) => transaction.date.value.getTime())));
        this.endDate = new Date(Math.max(...this.orderHistoryTableData.map((transaction) => transaction.date.value.getTime())));
      }
    },
  },
  created() {
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    this.populateTable();
  },
  components: {
    SortableTable,
    DatePicker,
  },
};
</script>

<style lang="scss">
.loyalty-table {
  .point-value {
    &-positive {
      color: $color-kws-green-100;
    }

    &-negative {
      color: $color-kws-signalred-100;
    }
  }

  th {
    position: relative;
  }
}
</style>
